// src/components/ProfessionalDetails.js
import React from 'react';
import { FaBriefcase } from 'react-icons/fa';
import { motion } from 'framer-motion';

const ProfessionalDetails = ({ formData, handleChange, handleArrayChange, errors }) => {
  console.log('📋 ProfessionalDetails rendered with:');
  console.log('  membershipType:', formData.membershipType);
  console.log('  employmentStatus:', formData.employmentStatus);
  console.log('  jobRole:', formData.jobRole);
  console.log('  errors:', errors);

  const getBorderClass = (field) => {
    const hasError = errors[field];
    console.log(`🔍 Field "${field}" has error:`, hasError, 'value:', formData[field]);
    return hasError ? 'is-invalid' : '';
  };

  // Check if user is a student member
  const isStudent = formData.membershipType === 'student';

  const specializations = ['Adult Critical Care', 'Pediatric Respiratory', 'Neonatal Care', 'Pulmonary Rehabilitation', 'Sleep Medicine', 'Diagnostics'];
  const fadeIn = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5 } };

  // Check if 'Others' is selected
  const isOthersSelected = formData.specialization.includes('Others');

  return (
    <motion.div {...fadeIn}>
      {/* Section Header */}
      <div className="d-flex align-items-center mb-4">
        <FaBriefcase className="me-3" style={{ fontSize: '2rem', color: '#6B8E23' }} />
        <h2 className="h3 fw-semibold mb-0" style={{ color: '#6B8E23' }}>Professional Details</h2>
      </div>

      {/* Professional Information Card */}
      <div className="card border-0 shadow-sm" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="card-body p-4">
          {/* Info message for student members */}
          {isStudent && (
            <div className="alert alert-info d-flex align-items-center mb-4" style={{ backgroundColor: '#e8f5e9', borderColor: '#6B8E23' }}>
              <i className="fas fa-info-circle me-2" style={{ color: '#6B8E23' }}></i>
              <small style={{ color: '#2C5234' }}>
                <strong>Student Member:</strong> Employment status and job role are not required for student membership. Please provide your years of experience in respiratory care or related studies.
              </small>
            </div>
          )}

          <div className="row g-3">
            <div className="col-md-6">
              <label htmlFor="license" className="form-label fw-medium">
                License Number <span className="text-muted fw-normal">(Optional)</span>
              </label>
              <input
                type="text"
                id="license"
                name="license"
                value={formData.license}
                onChange={(e) => handleChange('license', e.target.value)}
                className={`form-control ${getBorderClass('license')}`}
                placeholder="e.g., RT123456"
              />
            </div>

            <div className="col-md-6">
              <label htmlFor="governingBody" className="form-label fw-medium">
                Governing Body <span className="text-muted fw-normal">(Optional)</span>
              </label>
              <input
                type="text"
                id="governingBody"
                name="governingBody"
                value={formData.governingBody}
                onChange={(e) => handleChange('governingBody', e.target.value)}
                className={`form-control ${getBorderClass('governingBody')}`}
                placeholder="e.g., NBRC"
              />
            </div>

            {/* Employment Status - Hidden for student members */}
            {!isStudent && (
              <div className="col-md-6">
                <label htmlFor="employmentStatus" className="form-label fw-medium">
                  Employment Status <span className="text-danger">*</span>
                </label>
                <select
                  id="employmentStatus"
                  name="employmentStatus"
                  value={formData.employmentStatus}
                  onChange={(e) => handleChange('employmentStatus', e.target.value)}
                  className={`form-select ${getBorderClass('employmentStatus')}`}
                >
                  <option value="">Select Status</option>
                  <option value="employed">Employed</option>
                  <option value="self-employed">Self-employed</option>
                  <option value="unemployed">Unemployed</option>
                  <option value="student">Student</option>
                  <option value="retired">Retired</option>
                </select>
                {errors.employmentStatus && <div className="invalid-feedback d-block">{errors.employmentStatus}</div>}
              </div>
            )}

            {/* Primary Job Role - Hidden for student members and certain employment statuses */}
            {!isStudent && !['unemployed', 'student', 'retired'].includes(formData.employmentStatus) && (
              <div className="col-md-6">
                <label htmlFor="jobRole" className="form-label fw-medium">
                  Primary Job Role <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="jobRole"
                  name="jobRole"
                  value={formData.jobRole}
                  onChange={(e) => {
                    console.log('💼 Job role changed to:', e.target.value);
                    handleChange('jobRole', e.target.value);
                  }}
                  className={`form-control ${getBorderClass('jobRole')}`}
                  placeholder="e.g., Respiratory Therapist"
                  required
                />
                {errors.jobRole && <div className="invalid-feedback d-block">{errors.jobRole}</div>}
                <div className="form-text">This field is required for {formData.employmentStatus} members</div>
              </div>
            )}

            <div className="col-12">
              <label htmlFor="experience" className="form-label fw-medium">
                Years of Experience <span className="text-danger">*</span>
              </label>
              <select
                id="experience"
                name="experience"
                value={formData.experience}
                onChange={(e) => handleChange('experience', e.target.value)}
                className={`form-select ${getBorderClass('experience')}`}
              >
                <option value="">Select Range</option>
                <option value="0-2">0-2 years</option>
                <option value="3-5">3-5 years</option>
                <option value="6+">6+ years</option>
              </select>
              {errors.experience && <div className="invalid-feedback d-block">{errors.experience}</div>}
            </div>

            <div className="col-12">
              <label className="form-label fw-medium">Areas of Specialization</label>
              <div className="card border-0 mt-2" style={{ backgroundColor: 'white' }}>
                <div className="card-body p-3">
                  <div className="row g-3">
                    {specializations.map(spec => (
                      <div key={spec} className="col-md-6 col-lg-4">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            id={`spec-${spec}`}
                            checked={formData.specialization.includes(spec)}
                            onChange={(e) => handleArrayChange('specialization', spec, e.target.checked)}
                            className="form-check-input"
                            style={{
                              borderColor: '#6B8E23',
                              backgroundColor: formData.specialization.includes(spec) ? '#6B8E23' : 'white'
                            }}
                          />
                          <label htmlFor={`spec-${spec}`} className="form-check-label fw-medium">
                            {spec}
                          </label>
                        </div>
                      </div>
                    ))}

                    {/* Others checkbox */}
                    <div className="col-md-6 col-lg-4">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          id="spec-Others"
                          checked={isOthersSelected}
                          onChange={(e) => {
                            handleArrayChange('specialization', 'Others', e.target.checked);
                            if (!e.target.checked) {
                              handleChange('otherSpecialization', '');
                            }
                          }}
                          className="form-check-input"
                          style={{
                            borderColor: '#6B8E23',
                            backgroundColor: isOthersSelected ? '#6B8E23' : 'white'
                          }}
                        />
                        <label htmlFor="spec-Others" className="form-check-label fw-medium">
                          Others
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Text input shown when Others is checked */}
                  {isOthersSelected && (
                    <div className="mt-3">
                      <input
                        type="text"
                        id="otherSpecialization"
                        name="otherSpecialization"
                        value={formData.otherSpecialization || ''}
                        onChange={(e) => handleChange('otherSpecialization', e.target.value)}
                        className="form-control"
                        placeholder="Please describe your area of specialization"
                        style={{ borderColor: '#6B8E23' }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
export default ProfessionalDetails;